import React, { useState, useEffect } from 'react';
import { FiUser, FiBook, FiCheck, FiX, FiAlertCircle } from 'react-icons/fi';
import { adminAPI } from '../../../services/api';

const ExercisesCheckingPage = () => {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [questionTextsById, setQuestionTextsById] = useState({}); // id → текст вопроса (без номера из БД)
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [checkData, setCheckData] = useState({
        status: 'checked',
        score: 0,
        teacher_comment: ''
    });
    const [filter, setFilter] = useState({ studentId: '', status: 'submitted' });
    const [students, setStudents] = useState([]);

    useEffect(() => {
        loadData();
    }, [filter]);

    useEffect(() => {
        const exId = selectedSubmission?.exercise_id;
        if (!exId) {
            setQuestionTextsById({});
            return;
        }
        let cancelled = false;
        adminAPI.getExercise(exId).then((res) => {
            if (cancelled) return;
            const map = {};
            (res.data?.questions || []).forEach((q) => {
                const t = (q.question_text || '').trim();
                map[q.id] = t;
                map[String(q.id)] = t;
            });
            setQuestionTextsById(map);
        }).catch(() => {
            if (!cancelled) setQuestionTextsById({});
        });
        return () => { cancelled = true; };
    }, [selectedSubmission?.exercise_id, selectedSubmission?.id]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [submissionsRes, studentsRes] = await Promise.all([
                adminAPI.getExerciseSubmissions(filter),
                adminAPI.getStudents()
            ]);
            setSubmissions(submissionsRes.data.submissions || []);
            setStudents(studentsRes.data.students || []);
        } catch (error) {
            console.error('Ошибка загрузки данных:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectSubmission = async (submission) => {
        setSelectedSubmission(submission);
        // В селекте только «Проверено» и «Доработка». Если подставить status: 'submitted',
        // value не совпадает ни с одной опцией — при сохранении уходит снова submitted и БД не меняется.
        const formStatus = submission.status === 'needs_revision' ? 'needs_revision' : 'checked';
        setCheckData({
            status: formStatus,
            score: submission.score != null && submission.score !== '' ? Number(submission.score) : 0,
            teacher_comment: submission.teacher_comment || ''
        });
    };

    const handleCheck = async () => {
        if (!selectedSubmission) return;

        try {
            await adminAPI.checkExerciseSubmission(selectedSubmission.id, checkData);
            alert('Упражнение проверено!');
            setSelectedSubmission(null);
            loadData();
        } catch (error) {
            console.error('Ошибка проверки:', error);
            alert('Ошибка при проверке упражнения');
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            submitted: { bg: '#e6f7ff', color: '#1890ff', text: 'Отправлено' },
            checked: { bg: '#f6ffed', color: '#52c41a', text: 'Проверено' },
            needs_revision: { bg: '#fff1f0', color: '#ff4d4f', text: 'Требует доработки' }
        };
        const style = styles[status] || styles.submitted;
        
        return (
            <span style={{
                padding: '4px 12px',
                background: style.bg,
                color: style.color,
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
            }}>
                {style.text}
            </span>
        );
    };

    const renderAnswer = (answer, type, idToQuestionText) => {
        if (type === 'test') {
            return null;
        }
        
        if (typeof answer === 'object' && answer !== null && !Array.isArray(answer)) {
            return (
                <div style={{ fontSize: '14px' }}>
                    {Object.entries(answer).map(([key, value]) => {
                        const title = (idToQuestionText && (idToQuestionText[key] || idToQuestionText[Number(key)])) || '';
                        return (
                            <div key={key} style={{ marginBottom: '10px', padding: '8px', background: 'white', borderRadius: '6px' }}>
                                {title ? (
                                    <div style={{ fontWeight: '600', marginBottom: '6px' }}>{title}</div>
                                ) : null}
                                <div style={{ whiteSpace: 'pre-wrap' }}>
                                    {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }
        
        return <div style={{ fontSize: '14px' }}>{String(answer)}</div>;
    };

    if (loading) {
        return <div style={{ padding: '20px', textAlign: 'center' }}>Загрузка...</div>;
    }

    return (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '10px 20px 20px' }}>
            <h1 style={{ margin: '0 0 18px 0' }}>Проверка упражнений</h1>

            {/* Фильтры */}
            <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Студент</label>
                        <select
                            value={filter.studentId}
                            onChange={(e) => setFilter({ ...filter, studentId: e.target.value })}
                        >
                            <option value="">Все студенты</option>
                            {students.map(student => (
                                <option key={student.id} value={student.id}>
                                    {student.first_name} {student.last_name} ({student.username})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Статус</label>
                        <select
                            value={filter.status}
                            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                        >
                            <option value="">Все</option>
                            <option value="submitted">Отправлено</option>
                            <option value="checked">Проверено</option>
                            <option value="needs_revision">Требует доработки</option>
                        </select>
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: selectedSubmission ? '1fr 1fr' : '1fr', gap: '20px' }}>
                {/* Список упражнений */}
                <div>
                    {submissions.length === 0 ? (
                        <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
                            <p style={{ fontSize: '18px', color: '#999' }}>
                                {filter.status === 'submitted' 
                                    ? 'Нет новых упражнений для проверки' 
                                    : 'Нет упражнений с выбранными фильтрами'}
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gap: '15px' }}>
                            {submissions.map(submission => (
                                <div 
                                    key={submission.id} 
                                    className="card" 
                                    style={{ 
                                        padding: '20px',
                                        cursor: 'pointer',
                                        border: selectedSubmission?.id === submission.id ? '2px solid #4a90e2' : '1px solid #e0e0e0',
                                        background: selectedSubmission?.id === submission.id ? '#f0f8ff' : 'white'
                                    }}
                                    onClick={() => handleSelectSubmission(submission)}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                                                <FiUser color="#4a90e2" />
                                                <strong>{submission.first_name} {submission.last_name}</strong>
                                                {getStatusBadge(submission.status)}
                                            </div>
                                            <div style={{ fontSize: '14px', color: '#666', marginLeft: '24px' }}>
                                                {submission.username}
                                            </div>
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#999' }}>
                                            {new Date(submission.submitted_at).toLocaleString('ru-RU')}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                        <FiBook size={14} color="#666" />
                                        <span style={{ fontSize: '14px', color: '#666' }}>
                                            {submission.lesson_title}
                                        </span>
                                    </div>

                                    <div style={{ 
                                        padding: '10px', 
                                        background: '#f9f9f9', 
                                        borderRadius: '6px',
                                        marginTop: '10px'
                                    }}>
                                        <div style={{ fontWeight: '500', fontSize: '14px' }}>
                                            {submission.exercise_title}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                                            Тип: {submission.exercise_type}
                                        </div>
                                    </div>

                                    {submission.score !== null && (
                                        <div style={{ 
                                            marginTop: '10px', 
                                            fontSize: '14px',
                                            color: '#52c41a',
                                            fontWeight: '600'
                                        }}>
                                            Оценка: {submission.score}/100
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Форма проверки */}
                {selectedSubmission && (
                    <div className="card" style={{ padding: '25px', position: 'sticky', top: '20px', height: 'fit-content' }}>
                        <h3 style={{ marginTop: 0 }}>Проверка упражнения</h3>

                        <div style={{ 
                            marginBottom: '20px', 
                            padding: '15px', 
                            background: '#f9f9f9', 
                            borderRadius: '8px' 
                        }}>
                            <div style={{ fontWeight: '600', marginBottom: '10px' }}>
                                {selectedSubmission.exercise_title}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                                Студент: {selectedSubmission.first_name} {selectedSubmission.last_name}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                                Урок: {selectedSubmission.lesson_title}
                            </div>
                        </div>

                        <div style={{ 
                            marginBottom: '20px', 
                            padding: '15px', 
                            background: '#fff9e6', 
                            borderRadius: '8px',
                            border: '1px solid #ffe7ba'
                        }}>
                            <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>
                                Ответы студента:
                            </div>
                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {(() => {
                                    try {
                                        const answers = typeof selectedSubmission.answers === 'string' 
                                            ? JSON.parse(selectedSubmission.answers) 
                                            : selectedSubmission.answers;
                                        return renderAnswer(answers, selectedSubmission.exercise_type, questionTextsById);
                                    } catch (e) {
                                        return <div style={{ fontSize: '14px', color: '#999' }}>Не удалось отобразить ответы</div>;
                                    }
                                })()}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Статус</label>
                            <select
                                value={checkData.status}
                                onChange={(e) => setCheckData({ ...checkData, status: e.target.value })}
                            >
                                <option value="checked">Проверено</option>
                                <option value="needs_revision">Требует доработки</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Оценка (0-100)</label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={checkData.score}
                                onChange={(e) => setCheckData({ ...checkData, score: parseInt(e.target.value) || 0 })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Комментарий</label>
                            <textarea
                                value={checkData.teacher_comment}
                                onChange={(e) => setCheckData({ ...checkData, teacher_comment: e.target.value })}
                                placeholder="Оставьте комментарий для студента..."
                                rows={5}
                            />
                        </div>

                        <button
                            onClick={handleCheck}
                            style={{
                                width: '100%',
                                padding: '12px',
                                background: '#52c41a',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                        >
                            <FiCheck /> Сохранить проверку
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExercisesCheckingPage;
